import { useEffect, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { fetchDeployedBuildId, readCurrentBuildId } from "./buildId";

const POLL_INTERVAL_MS = 10 * 60_000;
/** Floor between checks, so returning to a tab repeatedly cannot turn into a request loop. */
const MIN_CHECK_GAP_MS = 60_000;

export type AppUpdateState = {
  updateAvailable: boolean;
};

/**
 * Notice a deploy and reload into it on the next navigation — the screen the user was on is
 * already gone at that point, so the reload destroys nothing. Someone who never navigates keeps
 * the old build until they do.
 *
 * `pathname` is a parameter (not `useLocation()`) so `shared` stays free of React Router;
 * the app-layer `AppUpdater` supplies it.
 */
export function useAppUpdate(pathname: string): AppUpdateState {
  // Read once: the document's bundle cannot change while the document is alive.
  const [currentBuildId] = useState(readCurrentBuildId);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [pathnameAtDetection, setPathnameAtDetection] = useState<string | null>(null);

  const mutationsInFlight = useIsMutating();

  // No check on mount — the document just loaded, so it *is* the deployed build.
  useEffect(() => {
    if (!currentBuildId || updateAvailable) return;

    let cancelled = false;
    let lastCheckedAt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const check = async () => {
      if (cancelled || document.hidden || !navigator.onLine) return;
      if (Date.now() - lastCheckedAt < MIN_CHECK_GAP_MS) return;
      lastCheckedAt = Date.now();

      try {
        const deployedBuildId = await fetchDeployedBuildId(controller.signal);
        // `null` is "no answer yet", not "no update".
        if (cancelled || !deployedBuildId) return;
        if (deployedBuildId !== currentBuildId) setUpdateAvailable(true);
      } catch {
        // Offline or aborted — the poll retries by itself.
      }
    };

    const tick = () => {
      void check();
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    const checkOnWake = () => {
      if (!document.hidden) void check();
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", checkOnWake);
    window.addEventListener("focus", checkOnWake);

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener("visibilitychange", checkOnWake);
      window.removeEventListener("focus", checkOnWake);
    };
  }, [currentBuildId, updateAvailable]);

  // Leaving the screen the user was on when the update turned up is the signal to reload.
  useEffect(() => {
    if (!updateAvailable) return;
    setPathnameAtDetection((current) => current ?? pathname);
  }, [updateAvailable, pathname]);

  // The mutation gate covers navigate-while-saving: a reload mid-request could abort a save the
  // user believes has happened.
  useEffect(() => {
    if (!updateAvailable || mutationsInFlight > 0) return;
    if (pathnameAtDetection === null || pathname === pathnameAtDetection) return;
    window.location.reload();
  }, [updateAvailable, mutationsInFlight, pathname, pathnameAtDetection]);

  return { updateAvailable };
}
